import React, { useCallback, useMemo, useReducer } from 'react';
import { Box, Flex, TextV2, useToast } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '@website-editor/customComponents/CustomJsonComponent/JsonComponent.types';
import { useGoToReorderView } from '@website-editor/customComponents/CustomJsonComponent/hooks/useJsonComponentNavigation';
import { useGetFormsForEvent } from '@website-editor/customComponents/CustomJsonComponent/components/FormDisplay/hooks/useGetFormsForEvent';
import { useJsonContainerTools } from '@website-editor/customComponents/CustomJsonComponent/hooks/useJsonBlobStorageUtils';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useTranslation } from '@shared/core';
import { BottomActionsWrapper } from '@shared/components/ScheduleEditor/components/BottomActionsWrapper';
import { BottomActions } from '@website-editor/features/custom-page/components/BottomActions/BottomActions';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { EditView } from './EditView';
import { FormDisplayData } from './FormDisplayGuest';

type SelectV1Response = {
  label: string;
  value: string;
} | null;

const FormDisplaySkeleton = () => {
  return (
    <Box padding="2rem">
      <Skeleton
        width="82px"
        height="18px"
        marginBottom="8px"
      />
      <Skeleton
        width="100%"
        height="48px"
        marginBottom="24px"
      />
      <Skeleton
        width="82px"
        height="18px"
        marginBottom="8px"
      />
      <Skeleton
        width="100%"
        height="52px"
        marginBottom="16px"
      />
      <Skeleton
        width="100%"
        height="36px"
        marginBottom="16px"
      />
    </Box>
  );
};

const initialState = (presentationData: FormDisplayData) => ({
  currentFormId: presentationData.formId ?? null,
  displayMode: presentationData.type ?? 'embedded',
  formInvalid: false,
  linkCTA: presentationData.linkCTA ?? '',
  linkHeader: presentationData.linkHeader ?? '',
  isOpen: false,
});

type FormDisplayEditState = {
  currentFormId: string | null;
  displayMode: 'embedded' | 'link';
  formInvalid: boolean;
  linkCTA: string;
  linkHeader: string;
  isOpen: boolean;
};

type REDUCER_ACTIONS =
  | { type: 'SET_FORM_ID'; payload: string | null }
  | { type: 'SET_DISPLAY_MODE'; payload: 'embedded' | 'link' }
  | { type: 'SET_FORM_INVALID'; payload: boolean }
  | { type: 'SET_LINK_CTA'; payload: string }
  | { type: 'SET_LINK_HEADER'; payload: string }
  | { type: 'TOGGLE_MENU' };

const reducer = (state: FormDisplayEditState, action: REDUCER_ACTIONS): FormDisplayEditState => {
  switch (action.type) {
    case 'SET_FORM_ID':
      return { ...state, currentFormId: action.payload };
    case 'SET_DISPLAY_MODE':
      return { ...state, displayMode: action.payload };
    case 'SET_FORM_INVALID':
      return { ...state, formInvalid: action.payload };
    case 'SET_LINK_CTA':
      return { ...state, linkCTA: action.payload };
    case 'SET_LINK_HEADER':
      return { ...state, linkHeader: action.payload };
    case 'TOGGLE_MENU':
      return { ...state, isOpen: !state.isOpen };
    default:
      return state;
  }
};

export const FormDisplayEdit = (props: AdminSidebarComponentProps) => {
  const { data } = props;
  const presentationData = data.content as FormDisplayData;

  const { goToReorderView } = useGoToReorderView();
  const { forms, loading } = useGetFormsForEvent(data.eventId);
  const { toast } = useToast();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { tq } = useTranslation('formCustomComponent');

  const [state, dispatch] = useReducer(reducer, presentationData, initialState);

  const handleMenuToggle = useCallback((value: SelectV1Response) => {
    dispatch({ type: 'TOGGLE_MENU' });
  }, []);

  const { saveJsonData } = useJsonContainerTools(props.eventHandle);

  const handleSaveChanges = useCallback(async () => {
    if (state.currentFormId) {
      const result = await saveJsonData({
        id: data.id,
        content: {
          ...presentationData,
          formId: state.currentFormId,
          type: state.displayMode,
          linkCTA: state.linkCTA,
          linkHeader: state.linkHeader,
        },
      });

      if (result.errors) {
        toast(tq`An error occurred while saving. Please try again.`);
        return;
      }

      dispatch({ type: 'SET_FORM_INVALID', payload: false });
      updateWebsitePreview();
      toast(tq`Form updated successfully!`);
      goToReorderView();
    } else {
      dispatch({ type: 'SET_FORM_INVALID', payload: true });
    }
  }, [state.currentFormId, saveJsonData, data.id, presentationData, state.displayMode, state.linkCTA, state.linkHeader, updateWebsitePreview, toast, tq, goToReorderView]);

  const hasUnsaved = useMemo(() => {
    return (
      state.currentFormId !== presentationData.formId ||
      state.displayMode !== presentationData.type ||
      state.linkCTA !== (presentationData.linkCTA ?? '') ||
      state.linkHeader !== (presentationData.linkHeader ?? '')
    );
  }, [state.currentFormId, presentationData, state.displayMode, state.linkCTA, state.linkHeader]);

  const renderContent = () => {
    if (forms.length === 0) {
      return (
        <Flex
          padding="2rem"
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <TextV2>{tq`No forms found. Please create a form first.`}</TextV2>
        </Flex>
      );
    } else {
      return (
        <EditView
          forms={forms}
          currentFormId={state.currentFormId}
          displayMode={state.displayMode}
          formInvalid={state.formInvalid}
          onFormChange={id => dispatch({ type: 'SET_FORM_ID', payload: id })}
          onDisplayModeChange={mode => dispatch({ type: 'SET_DISPLAY_MODE', payload: mode })}
          onCtaChange={cta => dispatch({ type: 'SET_LINK_CTA', payload: cta })}
          onHeaderChange={header => dispatch({ type: 'SET_LINK_HEADER', payload: header })}
          isOpen={state.isOpen}
          handleMenuToggle={handleMenuToggle}
          linkCTA={state.linkCTA}
          linkHeader={state.linkHeader}
        />
      );
    }
  };

  return (
    <Box padding="2rem">
      <SkeletonGroup
        isReady={!loading}
        placeholder={<FormDisplaySkeleton />}
      >
        {renderContent()}
      </SkeletonGroup>
      <BottomActionsWrapper>
        <BottomActions
          onCancel={goToReorderView}
          onSave={handleSaveChanges}
          isSaveDisabled={!hasUnsaved || loading}
        />
      </BottomActionsWrapper>
    </Box>
  );
};
